import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase/config';
import { 
    doc, 
    collection,
    onSnapshot, 
    setDoc,
    runTransaction,
    getDocs,
    deleteField,
    serverTimestamp,
    updateDoc
} from 'firebase/firestore';

import { 
    formatDate,
    formatNumber,
    TIPOS_MOVIMIENTO_ART_MASTER,
    recalcularLedgerContableArt
} from '../../../../utils/segurosUtils';

// Importamos la utilidad de borrado profundo desde las utilidades de Firebase
import { deleteCobranzaArtFull } from '../../../../firebase/firebaseUtils';

import ModalCobranzaART from '../../../ui/ModalCobranzaART';
import ModalNotificacion from '../../../ui/ModalNotificacion';
import ModalConfirmacion from '../../../ui/ModalConfirmacion';
import { getCobranzaStatusChip } from '../../../../utils/styles';

const IconTrendingUp = () => <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg>;
const IconTrendingDown = () => <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6L9 12.75l4.306-4.307a11.95 11.95 0 015.814-5.519l2.74 1.22m0 0l-5.94 2.281m5.94-2.28l-2.28 5.941" /></svg>;

/**
 * MovimientoObservacionesInline - Muestra directamente la lista de observaciones de forma estructurada.
 */
const MovimientoObservacionesInline = ({ clientId, contratoId, movimientoId }) => {
    const [observaciones, setObservaciones] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!clientId || !contratoId || !movimientoId) return;

        const path = `public/data/clientes/${clientId}/contratos/${contratoId}/historial_cobranza/${movimientoId}/observaciones`;
        const refColl = collection(db, path);

        const unsubscribe = onSnapshot(refColl, (snapshot) => {
            const data = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
            
            data.sort((a, b) => {
                const timeA = a.fechaCreacion?.seconds || 0;
                const timeB = b.fechaCreacion?.seconds || 0;
                return timeA - timeB;
            });

            setObservaciones(data);
            setLoading(false);
        }, (err) => {
            console.error("Error al escuchar notas de bitácora:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [clientId, contratoId, movimientoId]);

    if (loading) {
        return <div className="text-xs text-slate-400 italic animate-pulse text-left">Cargando bitácora de notas...</div>;
    }

    if (observaciones.length === 0) {
        return (
            <p className="text-slate-400 italic text-[11px] py-1 select-none text-left">
                Sin actualizaciones de bitácora ni notas de seguimiento cargadas.
            </p>
        );
    }

    return (
        <div className="space-y-2 text-left relative pl-4 border-l-2 border-slate-100 ml-1">
            {observaciones.map((o) => (
                <div key={o.id} className="relative flex flex-col gap-0.5 text-left">
                    <div className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full border-2 border-white bg-slate-400 shadow-sm" />
                    <p className="text-slate-700 text-xs font-semibold leading-relaxed italic">"{o.comentario}"</p>
                    <span className="block text-[8px] text-slate-400 font-bold uppercase mt-0.5 tracking-wider select-none">
                        ⏱️ {formatDate(o.fechaCreacion)}
                    </span>
                </div>
            ))}
        </div>
    );
};

/**
 * CobranzaARTTab - Panel de Cuenta Corriente y Libro Diario Contable.
 */
const CobranzaARTTab = ({ contrato, client }) => {
    const [contractLive, setContractLive] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [modalKey, setModalKey] = useState(0); 
    const [isExpanded, setIsExpanded] = useState(false);

    const [expandedMovId, setExpandedMovId] = useState(null);

    const [selectedMovement, setSelectedMovement] = useState(null);
    const [movementToDelete, setMovementToDelete] = useState(null);

    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [deletionConfirm, setDeletionConfirm] = useState({ show: false });
    const [isProcessing, setIsProcessing] = useState(false);

    const contratoId = contrato?.id;
    const clientId = client?.id || contrato?.clienteId;

    useEffect(() => {
        if (!contratoId || !clientId) return;

        setLoading(true);
        const contractRef = doc(db, `public/data/clientes/${clientId}/contratos`, contratoId);
        
        const unsubscribe = onSnapshot(contractRef, (docSnap) => {
            if (docSnap.exists()) {
                setContractLive({ id: docSnap.id, ...docSnap.data() });
            } else {
                setContractLive(null);
            }
            setLoading(false);
        }, (err) => {
            console.error("Error al escuchar contrato de ART en tiempo real:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [contratoId, clientId]);

    useEffect(() => {
        if (!contratoId || !clientId) return;

        const historialRef = collection(db, `public/data/clientes/${clientId}/contratos/${contratoId}/historial_cobranza`);
        
        const unsubscribe = onSnapshot(historialRef, (snapshot) => {
            const data = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
            
            data.sort((a, b) => {
                const dateA = a.fechaMovimiento || '';
                const dateB = b.fechaMovimiento || '';
                if (dateA !== dateB) {
                    return dateB.localeCompare(dateA); 
                }
                const timeA = a.fechaCarga?.seconds || 0;
                const timeB = b.fechaCarga?.seconds || 0;
                return timeB - timeA;
            });

            setHistorial(data);
        }, (err) => {
            console.error("Error al escuchar historial_cobranza de ART:", err);
        });

        return () => unsubscribe();
    }, [contratoId, clientId]);

    const handleResetSaldo = async () => {
        setDeletionConfirm({ show: false });
        setIsProcessing(true); // Bloquea la UI e inyecta la pantalla de carga limpia de inmediato
        try {
            const contractPath = `public/data/clientes/${clientId}/contratos/${contratoId}`;
            const contractRef = doc(db, contractPath);
            const historialRef = collection(db, `${contractPath}/historial_cobranza`);

            // 1. Borramos recursivamente las observaciones de todos los movimientos antes de eliminar los hitos
            const movsSnap = await getDocs(historialRef);
            for (const mDoc of movsSnap.docs) {
                await deleteCobranzaArtFull(contractPath, mDoc.id);
            }

            // 2. Limpieza real con deleteField en la raíz del contrato para no dejar rastro de deudas previas
            await updateDoc(contractRef, {
                artDeudaGlobal: deleteField(),
                tieneDeuda: deleteField(), 
                artEstadoDeuda: deleteField(),
                artEstadoGestion: deleteField(),
                artEstadoContrato: deleteField(),
                artUltimoMovimientoTipo: deleteField(),
                artUltimoMovimientoMonto: deleteField(),
                artUltimoMovimientoFecha: deleteField(),
                artUltimoReclamoFecha: deleteField(),
                artUltimoMovimientoResponsable: deleteField(),
                artUltimaActualizacion: serverTimestamp()
            });

            setNotification({ show: true, message: 'La cuenta corriente se ha purgado por completo. Saldos y observaciones removidos de forma atómica.', type: 'success' });
        } catch (error) {
            console.error("Error al eliminar balance de ART:", error);
            setNotification({ show: true, message: 'Error al intentar purgar la cuenta de ART.', type: 'error' });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteIndividualMovement = async () => {
        if (!movementToDelete) return;
        setIsProcessing(true); // Oculta los desfasajes mientras se recalculan los deudores
        try {
            const contractPath = `public/data/clientes/${clientId}/contratos/${contratoId}`;
            const contractRef = doc(db, contractPath);
            const historialCollRef = collection(db, `${contractPath}/historial_cobranza`);

            const querySnapshot = await getDocs(historialCollRef);
            const docsList = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            // Filtrar únicamente el elemento a borrar
            const filteredList = docsList.filter(m => m.id !== movementToDelete.id);

            // Re-proyectar secuencialmente toda la cuenta corriente usando nuestro motor centralizado
            const finalLedger = recalcularLedgerContableArt(filteredList, contractLive?.artEstadoContrato || 'Normalizado');
            const finalGlobalDeuda = finalLedger.length > 0 ? finalLedger[finalLedger.length - 1].saldoResultante : 0;

            const updatesToSave = finalLedger.map(mov => ({
                ref: doc(db, `${contractPath}/historial_cobranza`, mov.id),
                data: {
                    ...mov,
                    fechaCarga: mov.fechaCarga instanceof Date ? mov.fechaCarga : (mov.fechaCarga?.seconds ? new Date(mov.fechaCarga.seconds * 1000) : new Date())
                }
            }));

            // Usamos nuestra utilidad centralizada 'deleteCobranzaArtFull' para limpiar el movimiento seleccionado y sus observaciones
            await deleteCobranzaArtFull(contractPath, movementToDelete.id);

            await runTransaction(db, async (transaction) => {
                const sfContract = await transaction.get(contractRef);
                if (!sfContract.exists()) throw new Error("Contrato maestro no encontrado.");

                // Guardar los saldos corregidos de toda la cadena
                for (const item of updatesToSave) {
                    transaction.set(item.ref, item.data, { merge: true });
                }

                // Obtener el hito temporalmente más reciente para actualizar metadatos del contrato comercial
                const sortedDesc = [...finalLedger].sort((a, b) => {
                    const dateA = a.fechaMovimiento || '';
                    const dateB = b.fechaMovimiento || '';
                    if (dateA !== dateB) return dateB.localeCompare(dateA);
                    
                    const timeA = a.fechaCarga?.seconds || (a.fechaCarga instanceof Date ? a.fechaCarga.getTime() / 1000 : Date.now() / 1000);
                    const timeB = b.fechaCarga?.seconds || (b.fechaCarga instanceof Date ? b.fechaCarga.getTime() / 1000 : Date.now() / 1000);
                    return timeB - timeA;
                });

                const latestMov = sortedDesc[0];

                if (finalLedger.length === 0) {
                    // Si ya no quedan movimientos, limpiamos los metadatos de la raíz de forma absoluta
                    transaction.update(contractRef, {
                        artDeudaGlobal: deleteField(),
                        tieneDeuda: deleteField(),
                        artEstadoContrato: deleteField(),
                        artEstadoGestion: deleteField(),
                        artEstadoDeuda: deleteField(),
                        artUltimoMovimientoTipo: deleteField(),
                        artUltimoMovimientoMonto: deleteField(),
                        artUltimoMovimientoFecha: deleteField(),
                        artUltimoReclamoFecha: deleteField(),
                        artUltimoMovimientoResponsable: deleteField(),
                        artUltimaActualizacion: serverTimestamp()
                    });
                } else {
                    const calculatedEstadoDeuda = finalGlobalDeuda > 0 ? (latestMov?.estadoDeuda === 'Incobrable' ? 'Incobrable' : 'Con Deuda') : 'Al día';

                    transaction.update(contractRef, {
                        artDeudaGlobal: finalGlobalDeuda,
                        tieneDeuda: finalGlobalDeuda > 0,
                        artEstadoContrato: latestMov?.estadoContrato || 'Normalizado',
                        artEstadoGestion: latestMov?.estadoGestion || 'Pendiente',
                        artEstadoDeuda: calculatedEstadoDeuda,
                        artUltimoMovimientoTipo: latestMov?.tipoMovimiento || 'Disminución de Deuda',
                        artUltimoMovimientoMonto: latestMov?.importe || 0,
                        artUltimoMovimientoFecha: latestMov?.fechaMovimiento || new Date().toISOString().substring(0, 10),
                        artUltimoMovimientoResponsable: latestMov ? (latestMov.tipoMovimiento === 'Pago Recibido' ? (latestMov.quienCobro || '') : (latestMov.quienInformo || '')) : 'SISTEMA',
                        artUltimaActualizacion: serverTimestamp()
                    });
                }
            });

            setNotification({ show: true, message: 'Hito contable removido. El Ledger histórico de saldos fue recalculado con éxito.', type: 'success' });
        } catch (err) {
            console.error("Error al eliminar movimiento de ART:", err);
            setNotification({ show: true, message: 'Error al intentar eliminar el movimiento seleccionado.', type: 'error' });
        } finally {
            setIsProcessing(false);
            setMovementToDelete(null);
        }
    };

    const handleOpenModal = (mov = null) => {
        setSelectedMovement(mov);
        setModalKey(Date.now());
        setShowModal(true);
    };

    const tieneExpediente = contractLive && (contractLive.artDeudaGlobal > 0 || historial.length > 0);

    return (
        <div className="space-y-6 animate-fade-in font-sans text-left pb-10">
            
            {/* CUADRO CONSOLIDADO */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-sm relative overflow-hidden shrink-0">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-500"></div>
                <div>
                    <p className="text-[10px] font-black text-red-500 uppercase tracking-widest mb-1">Deuda Consolidada Corriente (ART)</p>
                    <div className="flex items-baseline gap-2 text-left">
                        <span className="text-3xl font-black text-slate-900 tracking-tighter">
                            $ {formatNumber(contractLive?.artDeudaGlobal)}
                        </span>
                        {contractLive?.artUltimaActualizacion && (
                            <span className="text-[10px] text-slate-400 font-bold">
                                (Act: {formatDate(contractLive.artUltimaActualizacion)})
                            </span>
                        )}
                    </div>
                </div>
                
                <div className="flex items-center gap-3 w-full sm:w-auto select-none justify-end">
                    <button 
                        onClick={() => handleOpenModal(null)} 
                        disabled={isProcessing}
                        className="flex-grow sm:flex-grow-0 bg-red-600 hover:bg-red-700 disabled:bg-slate-350 text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 shadow-lg uppercase tracking-widest"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
                        {tieneExpediente ? 'Actualizar Cuenta Corriente' : 'Cargar Saldo Inicial'}
                    </button>
                </div>
            </div>

            {/* TABLA PRINCIPAL - Posee desplazamiento scroll overflow con acciones fijas para evitar doble fila */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto custom-scrollbar relative">
                <table className="w-full text-left border-collapse text-[13px] min-w-[1250px]">
                    <thead className="bg-slate-900 text-white uppercase font-bold text-[9px] tracking-widest select-none">
                        <tr>
                            <th className="p-4 w-12 text-center"></th>
                            <th className="p-4 w-32 text-center whitespace-nowrap">Última Operación</th>
                            <th className="p-4 w-44 text-right whitespace-nowrap">Saldo en Cartera</th>
                            <th className="p-4 w-40 text-center whitespace-nowrap">Última Acción</th>
                            <th className="p-4 w-52 text-center whitespace-nowrap">Estado Contrato</th>
                            <th className="p-4 w-40 text-center whitespace-nowrap">Estado Deuda</th>
                            <th className="p-4 w-36 text-center whitespace-nowrap">Fecha Notificación</th>
                            <th className="p-4 w-40 text-center whitespace-nowrap">Quién Informó</th>
                            <th className="p-4 w-40 text-center whitespace-nowrap">Quién Cobró</th>
                            <th className="p-4 w-36 text-center whitespace-nowrap">Fecha Pago</th>
                            <th className="p-4 w-28 text-right pr-8 sticky right-0 bg-slate-900 shadow-[-5px_0_10px_rgba(0,0,0,0.3)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {/* REGla DE NEGOCIO Y UX: Bloqueamos los renderizados ruidosos individuales durante la purga en lote */}
                        {loading || isProcessing ? (
                            <tr>
                                <td colSpan="11" className="p-16 text-center text-slate-400 font-bold animate-pulse uppercase tracking-widest">
                                    {isProcessing ? 'Procesando transacciones y recalibrando balances...' : 'Sincronizando cuenta corriente de ART...'}
                                </td>
                            </tr>
                        ) : !tieneExpediente ? (
                            <tr><td colSpan="11" className="p-16 text-center text-slate-400 italic font-medium">Sin deudas ni saldos activos registrados para este contrato de ART.</td></tr>
                        ) : (
                            <React.Fragment>
                                <tr onClick={() => setIsExpanded(!isExpanded)} className={`hover:bg-slate-50/70 transition-colors cursor-pointer group ${isExpanded ? 'bg-blue-50/40' : ''}`}>
                                    <td className="p-4 text-center">
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? 'rotate-90 text-blue-600' : 'text-slate-300 group-hover:text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                                    </td>
                                    <td className="p-4 font-bold text-slate-500 tabular-nums text-center whitespace-nowrap">
                                        {formatDate(contractLive?.artUltimoMovimientoFecha || contractLive?.artUltimaActualizacion)}
                                    </td>
                                    
                                    <td className="p-4 text-right">
                                        <span className="font-black text-blue-700 whitespace-nowrap text-[14px]">
                                            $ {formatNumber(contractLive?.artDeudaGlobal)}
                                        </span>
                                    </td>
                                    
                                    <td className="p-4 text-center whitespace-nowrap font-bold">
                                        {(() => {
                                            const config = TIPOS_MOVIMIENTO_ART_MASTER[contractLive?.artUltimoMovimientoTipo];
                                            if (!config) return <span className="text-slate-400">-</span>;
                                            return <span className={config.textStyle}>{config.label}</span>;
                                        })()}
                                    </td>

                                    <td className="p-3 text-center font-bold text-slate-900 text-xs">
                                        <span>
                                            {contractLive?.artEstadoContrato || 'Normalizado'}
                                        </span>
                                    </td>

                                    <td className="p-4 text-center whitespace-nowrap font-bold">
                                        <span className={`${getCobranzaStatusChip(contractLive?.artEstadoDeuda || 'S/D')} whitespace-nowrap`}>
                                            {contractLive?.artEstadoDeuda || 'S/D'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-center text-slate-500 font-medium tabular-nums whitespace-nowrap">
                                        {formatDate(contractLive?.artUltimoMovimientoTipo !== 'Pago Recibido' ? contractLive?.artUltimoMovimientoFecha : '-')}
                                    </td>
                                    <td className="p-4 text-center whitespace-nowrap font-semibold text-slate-600">
                                        {contractLive?.artUltimoMovimientoTipo !== 'Pago Recibido' ? (contractLive?.artUltimoMovimientoResponsable || '-') : '-'}
                                    </td>
                                    <td className="p-4 text-center whitespace-nowrap">
                                        {contractLive?.artUltimoMovimientoTipo === 'Pago Recibido' ? (
                                            <div className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide inline-block bg-emerald-50 text-emerald-700">
                                                {contractLive?.artUltimoMovimientoResponsable || 'OPERADOR'}
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td className="p-4 text-center text-slate-500 font-medium tabular-nums whitespace-nowrap">
                                        {contractLive?.artUltimoMovimientoTipo === 'Pago Recibido' ? formatDate(contractLive?.artUltimoMovimientoFecha) : '-'}
                                    </td>
                                    
                                    <td className={`p-4 text-right pr-6 sticky right-0 z-10 transition-colors shadow-[-5px_0_10px_rgba(0,0,0,0.02)] ${isExpanded ? 'bg-[#f8faff]' : 'bg-white group-hover:bg-slate-50'}`}>
                                        <div className="flex justify-end gap-1.5 opacity-40 group-hover:opacity-100 transition-opacity">
                                            <button onClick={(e) => { e.stopPropagation(); handleOpenModal(null); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm border border-slate-100" title="Actualizar / Aplicar Movimiento"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                            <button onClick={(e) => { e.stopPropagation(); setDeletionConfirm({ show: true }); }} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-white rounded-lg shadow-sm border border-slate-100" title="Restablecer Balance a Cero"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                        </div>
                                    </td>
                                </tr>

                                {/* SECCIÓN EXPANDIBLE: LIBRO DIARIO DE AUDITORÍA */}
                                {isExpanded && (
                                    <tr className="bg-slate-50/50 border-b border-slate-100 animate-fade-in">
                                        <td colSpan="11" className="px-8 py-6 text-left">
                                            <div className="space-y-6">
                                                <div className="flex flex-col gap-1 select-none">
                                                    <h4 className="font-black text-slate-700 uppercase text-[10px] tracking-widest flex items-center gap-2">
                                                        📊 Libro Diario Contable de Cuenta Corriente (ART)
                                                    </h4>
                                                    <p className="text-[11px] text-slate-400">Auditoría completa de variaciones, cobros y notas históricas registradas.</p>
                                                </div>

                                                <div className="overflow-visible border border-slate-250 rounded-xl bg-white shadow-sm">
                                                    <table className="w-full text-left text-xs">
                                                        <thead className="bg-slate-50 text-slate-650 font-bold uppercase text-[9px] border-b border-slate-200 select-none">
                                                            <tr>
                                                                <th className="p-3 w-20">Fecha Mov.</th>
                                                                <th className="p-3 w-44">Tipo de Operación</th>
                                                                <th className="p-3 w-44 text-center">Estado Contrato</th>
                                                                <th className="p-3 w-32 text-center">Estado Gestión</th>
                                                                <th className="p-3 w-32 text-center">Estado Deuda</th>
                                                                <th className="p-3 w-32 text-right">Saldo Previo</th>
                                                                <th className="p-3 w-32 text-right">Importe Ajuste</th>
                                                                <th className="p-3 w-32 text-right bg-slate-50/50">Saldo Resultante</th>
                                                                <th className="p-3 w-28 text-center">Acciones</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100">
                                                            {historial.length > 0 ? (
                                                                historial.map((h, idx) => {
                                                                    const config = TIPOS_MOVIMIENTO_ART_MASTER[h.tipoMovimiento];
                                                                    const esAbono = config ? !config.estiloDebito : (h.tipoMovimiento === 'Pago Recibido' || h.tipoMovimiento === 'Disminución de Deuda');
                                                                    const esSistema = h.quienInformo === 'SISTEMA' || String(h.comentario || '').includes('[SISTEMA]');
                                                                    const isMovExpanded = expandedMovId === h.id;
                                                                    
                                                                    return (
                                                                        <React.Fragment key={h.id || idx}>
                                                                            
                                                                            <tr 
                                                                                onClick={() => setExpandedMovId(isMovExpanded ? null : h.id)}
                                                                                className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${esSistema ? 'bg-slate-50/30' : ''} ${isMovExpanded ? 'bg-blue-50/30 font-semibold' : ''}`}
                                                                                title="Haga clic para desplegar las observaciones e historial de notas"
                                                                            >
                                                                                <td className="p-3 text-slate-500 font-medium tabular-nums">
                                                                                    {formatDate(h.fechaMovimiento || h.fechaCarga)}
                                                                                </td>
                                                                                
                                                                                <td className="p-3">
                                                                                    <div className="flex items-center gap-1.5 font-bold">
                                                                                        {esAbono ? <IconTrendingDown /> : <IconTrendingUp />}
                                                                                        <span className={esAbono ? 'text-emerald-700' : 'text-red-700'}>
                                                                                            {config ? config.label : h.tipoMovimiento}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>

                                                                                <td className="p-3 text-center font-bold text-slate-900 text-xs">
                                                                                    {h.estadoContrato || 'Normalizado'}
                                                                                </td>

                                                                                <td className="p-3 text-center whitespace-nowrap">
                                                                                    <span className={`${getCobranzaStatusChip(h.estadoGestion || 'Pendiente')} text-[11px] font-bold`}>
                                                                                        {h.estadoGestion || 'Pendiente'}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="p-3 text-center whitespace-nowrap">
                                                                                    <span className={`${getCobranzaStatusChip(h.estadoDeuda || 'Con Deuda')} text-[11px] font-bold`}>
                                                                                        {h.estadoDeuda || 'Con Deuda'}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="p-3 text-right font-medium text-slate-500 tabular-nums">
                                                                                    $ {formatNumber(h.saldoAnterior)}
                                                                                </td>
                                                                                <td className="p-3 text-right font-black tabular-nums">
                                                                                    <span className={esAbono ? 'text-emerald-600' : 'text-red-600'}>
                                                                                        {esAbono ? '-' : '+'} $ {formatNumber(h.importe)}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="p-3 text-right font-black tabular-nums bg-slate-50/30 text-slate-800">
                                                                                    $ {formatNumber(h.saldoResultante)}
                                                                                </td>
                                                                                
                                                                                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                                                    {!esSistema && (
                                                                                        <div className="flex justify-center gap-1.5 select-none">
                                                                                            <button 
                                                                                                onClick={() => handleOpenModal(h)} 
                                                                                                className="p-1 bg-white hover:bg-blue-50 text-blue-600 rounded-lg border border-slate-250 shadow-sm transition-all"
                                                                                                title="Editar este movimiento"
                                                                                            >
                                                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                                                                                                </svg>
                                                                                            </button>
                                                                                            <button 
                                                                                                onClick={() => setMovementToDelete(h)} 
                                                                                                className="p-1 bg-white hover:bg-red-50 text-red-600 rounded-lg border border-slate-250 shadow-sm transition-all"
                                                                                                title="Eliminar este hito contable"
                                                                                            >
                                                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                                                                                </svg>
                                                                                            </button>
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                            </tr>

                                                                            {/* SECCIÓN EXPANDIBLE REDISEÑADA */}
                                                                            {isMovExpanded && (
                                                                                <tr className="bg-[#f0f4f8]/30 animate-fade-in" onClick={(e) => e.stopPropagation()}>
                                                                                    <td colSpan={11} className="p-4 pl-12 bg-slate-50/60 border-l-4 border-blue-500">
                                                                                        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-md max-w-2xl text-left">
                                                                                            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5 mb-3.5 select-none">
                                                                                                <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5">
                                                                                                    <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>
                                                                                                    Detalles de la Transacción
                                                                                                </span>
                                                                                            </div>

                                                                                            {/* Comentario principal de carga de la transacción */}
                                                                                            {h.comentario && (
                                                                                                <div className="mb-4 p-3 bg-blue-50/40 border border-blue-100/60 rounded-xl text-left">
                                                                                                    <span className="block text-[8px] font-black text-blue-500 uppercase tracking-widest mb-1 select-none">Descripción Principal</span>
                                                                                                    <p className="text-slate-800 text-[12px] font-semibold leading-relaxed">"{h.comentario}"</p>
                                                                                                </div>
                                                                                            )}

                                                                                            {/* Timeline de notas específicas de bitácora */}
                                                                                            <div className="space-y-2 mt-4 text-left">
                                                                                                <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2 select-none">Bitácora de Observaciones</span>
                                                                                                <MovimientoObservacionesInline 
                                                                                                    clientId={clientId} 
                                                                                                    contratoId={contratoId} 
                                                                                                    movimientoId={h.id} 
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                })
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan={11} className="p-8 text-center text-slate-400 italic font-medium">
                                                                        Sin transacciones ni variaciones registradas en el libro diario.
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ESTILOS DE BARRA Y ANIMACIÓN */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>

            {/* MODALES AUXILIARES */}
            <ModalCobranzaART 
                key={modalKey} 
                show={showModal} 
                onClose={() => setShowModal(false)} 
                contrato={contrato} 
                clientData={client} 
                initialData={selectedMovement} 
                onSuccess={async (mensaje) => { 
                    setShowModal(false); 
                    setSelectedMovement(null);
                    setNotification({show: true, message: mensaje || 'La cuenta corriente de ART se actualizó con total éxito.', type: 'success'}); 
                }} 
            />

            <ModalNotificacion show={notification.show} onClose={() => setNotification({ ...notification, show: false })} message={notification.message} type={notification.type} />
            
            <ModalConfirmacion show={deletionConfirm.show} onClose={() => setDeletionConfirm({ show: false })} onConfirm={handleResetSaldo} title="Restablecer Balance de Cuenta" message="¿Estás seguro de que deseas purgar y reestablecer por completo la cuenta corriente de este contrato de ART? Esta acción eliminará físicamente todos los hitos y observaciones de bitácora." />

            <ModalConfirmacion 
                show={!!movementToDelete} 
                onClose={() => setMovementToDelete(null)} 
                onConfirm={handleDeleteIndividualMovement} 
                title="Eliminar Movimiento Financiero" 
                message={`¿Estás completamente seguro de eliminar este registro de ${movementToDelete?.tipoMovimiento}? Esta acción revertirá de forma automática su impacto financiero sobre la deuda de ART viva en cartera, recalculando secuencialmente la cadena de saldos.`} 
            />
        </div>
    );
};

export default CobranzaARTTab;